import { useState, useEffect } from 'react';
import baseapi from '../../../services/baseapi';
import { Loader2, ShieldAlert } from 'lucide-react';
import { showError } from '../../../utils/toast';
import AgentSSH from '../../Agent/AgentSSH';

interface SwitchSSHProps {
    asset_id: string | number;
    client_id: string | number;
}

export default function SwitchSSH({ asset_id, client_id }: SwitchSSHProps) {
    const [loading, setLoading] = useState(true);
    const [agentId, setAgentId] = useState<number | null>(null);

    useEffect(() => {
        let isMounted = true;
        (async () => {
            setLoading(true);
            try {
                // First check if asset data stored in localStorage has agent_id
                const info = JSON.parse(sessionStorage.getItem("internal_ids") || "{}");
                if (info && info.agent_id) {
                    if (isMounted) setAgentId(Number(info.agent_id));
                    setLoading(false);
                    return;
                }

                // Query /agents endpoint to find probe agent for client_id
                const res = await baseapi.get("/agents");
                if (res.data?.status && Array.isArray(res.data?.data)) {
                    const clientAgents = res.data.data.filter((a: any) =>
                        client_id ? String(a.client_id) === String(client_id) : true
                    );
                    // Find probe agent first
                    const probeAgent = clientAgents.find((a: any) => (a.agent_type || '').toLowerCase() === 'probe');
                    const targetAgent = probeAgent || clientAgents[0] || res.data.data.find((a: any) => (a.agent_type || '').toLowerCase() === 'probe');

                    if (targetAgent && targetAgent.id) {
                        if (isMounted) setAgentId(targetAgent.id);
                    }
                }
            } catch (e: any) {
                showError(e.message || "Failed to load agent info");
            } finally {
                if (isMounted) setLoading(false);
            }
        })();

        return () => {
            isMounted = false;
        };
    }, [asset_id, client_id]);

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <Loader2 className="w-6 h-6 animate-spin text-orange-500" />
            </div>
        );
    }

    if (!agentId) {
        return (
            <div className="flex flex-col items-center justify-center h-64 gap-2 opacity-60">
                <ShieldAlert className="w-10 h-10 text-orange-400" />
                <p className="text-sm text-gray-200 font-bold uppercase tracking-widest">No associated Probe Agent found for SSH</p>
            </div>
        );
    }

    return (
        <div className="w-full h-full min-h-[500px]">
            <AgentSSH agentId={agentId} clientId={client_id} hideHeader={true} />
        </div>
    );
}
